import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { addFranchisee, updateFranchiseeStatus, editFranchisee, DuplicateFranchiseeNameError } from '../services/franchiseeService';
import { uploadFiles, deleteFile, getSignedUrl } from '../services/fileService';
import { useNavigate, Link } from 'react-router-dom';
import { FaFileUpload, FaPaperclip, FaTimes, FaFile, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel } from 'react-icons/fa';
import { fetchFileTypes, isCustomFileType, filterAttachmentFileTypes } from '../config/fileTypes';
import ConfirmationModal from '../components/ConfirmationModal';

const AddFranchiseePage = () => {
  const { register, handleSubmit, setValue, control, formState: { errors, isSubmitting } } = useForm({
    mode: 'onBlur', // Validate on blur
    defaultValues: {
        franchiseCode: '',
        franchiseName: '',
        email: '',
        phoneNumber: '',
        abn: '',
        gstRegistered: 'No',
        country: 'Australia',
        firstName: '',
        lastName: '',
        bsbNumber: '',
        bankAccountNumber: '',
        bankAccountName: '',
        territory: '',
        addressLine1: '',
        addressLine2: '',
        state: '',
        postalCode: '',
        xeroContactId: '', // Added xeroContactId
        isExternal: false, // External franchisee flag (defaults to Regional)
    }
  });
  const navigate = useNavigate();
  const [apiError, setApiError] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, file: null });
  const [deleting, setDeleting] = useState(false);
  const [fileTypes, setFileTypes] = useState([]);
  const [loadingFileTypes, setLoadingFileTypes] = useState(true);
  const fileInputRef = useRef(null);
  const [duplicateNameModal, setDuplicateNameModal] = useState({ show: false, existingFranchisee: null, pendingPayload: null });
  const [isSuspendingFranchisee, setIsSuspendingFranchisee] = useState(false);

  // Load file types on component mount
  useEffect(() => {
    const loadFileTypes = async () => {
      try {
        setLoadingFileTypes(true);
        const [types] = await Promise.all([
          fetchFileTypes(),
        ]);
        setFileTypes(filterAttachmentFileTypes(types));
      } catch (error) {
        console.error('Error loading file types:', error);
        // Set fallback values
        setFileTypes([{ value: 'other', label: 'Other' }]);
      } finally {
        setLoadingFileTypes(false);
      }
    };

    loadFileTypes();
  }, []);

    const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf': return <FaFilePdf className="text-red-500" />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': return <FaFileImage className="text-blue-500" />;
      case 'doc': case 'docx': return <FaFileWord className="text-blue-600" />;
      case 'xls': case 'xlsx': return <FaFileExcel className="text-green-600" />;
      default: return <FaFile className="text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (10MB limit)
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      alert('File size must be less than 10MB');
      return;
    }

    // Add file to attached files list with default values
    const newFile = {
      id: Date.now() + Math.random(), // Simple unique ID
      file: file,
      category: fileTypes[0]?.value || 'other', // Default to first file type
      customType: '', // Empty initially - user will fill this in if needed
      expiryDate: '', // Empty initially - user will fill this in
      name: file.name,
      size: file.size
    };


    setAttachedFiles(prev => [...prev, newFile]);
    
    // Clear the input
    event.target.value = '';
  };

  const updateFileCategory = (fileId, category) => {
    setAttachedFiles(prev =>
      prev.map(file =>
        file.id === fileId ? { ...file, category: category.trim(), customType: category === 'other' ? file.customType : '' } : file
      )
    );
  };

  const updateFileCustomType = (fileId, customType) => {
    setAttachedFiles(prev =>
      prev.map(file =>
        file.id === fileId ? { ...file, customType: customType.trim() } : file
      )
    );
  };

  const updateFileExpiryDate = (fileId, expiryDate) => {
    setAttachedFiles(prev =>
      prev.map(file =>
        file.id === fileId ? { ...file, expiryDate } : file
      )
    );
  };

  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleViewFile = async (fileUrl) => {
    if (!fileUrl) return;
    try {
      const signedUrl = await getSignedUrl(fileUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening file:', err);
    }
  };

  const removeUploadedFile = (fileId) => {
    const fileToDelete = uploadedFiles.find(f => f.id === fileId);
    if (fileToDelete) {
      setDeleteConfirmModal({ show: true, file: fileToDelete });
    }
  };

  const confirmDeleteFile = async () => {
    const fileToDelete = deleteConfirmModal.file;
    if (!fileToDelete) return;

    setDeleting(true);
    try {
      // Call the delete API endpoint with file URL (like EditClientPage)
      await deleteFile(fileToDelete.url);
      
      // Remove the file from the uploaded files list
      setUploadedFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
      
      // Close the modal
      setDeleteConfirmModal({ show: false, file: null });
      
      // Show success message
      alert('File deleted successfully!');
    } catch (error) {
      console.error('File deletion error:', error);
      alert(error.message || 'Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteFile = () => {
    setDeleteConfirmModal({ show: false, file: null });
  };

  const handleUploadFiles = async () => {
    if (attachedFiles.length === 0) {
      alert('No files to upload');
      return;
    }

    console.log('attachedFiles before upload:', attachedFiles);

    // Check if all files have required fields
    const filesWithoutCategory = attachedFiles.filter(file => !file.category || file.category.trim() === '');
    if (filesWithoutCategory.length > 0) {
      alert('Please select a file type for all files before uploading');
      return;
    }

    const filesWithoutCustomType = attachedFiles.filter(file =>
      file.category === 'other' && (!file.customType || file.customType.trim() === '')
    );
    if (filesWithoutCustomType.length > 0) {
      alert('Please specify the custom type for "Other" file types before uploading');
      return;
    }

    const filesWithoutExpiryDate = attachedFiles.filter(file => !file.expiryDate);
    if (filesWithoutExpiryDate.length > 0) {
      alert('Please provide an expiry date for all files before uploading');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const response = await uploadFiles(attachedFiles);
      console.log('Upload successful:', response);
      
      // Handle the response structure - response.data.urls contains the URLs
      const urls = response.data?.urls || [];
      
      // Move uploaded files to uploadedFiles array with URLs
      const filesWithUrls = attachedFiles.map((file, index) => ({
        id: Date.now() + Math.random() + index,
        name: file.name,
        url: urls[index] || '',
        size: file.size,
        category: file.category,
        customType: file.customType,
        expiryDate: file.expiryDate,
        uploaded: true
      }));
      
      setUploadedFiles(prev => [...prev, ...filesWithUrls]);
      
      // Clear attached files after successful upload
      setAttachedFiles([]);
      
      // Show success message
      alert(`${urls.length || attachedFiles.length} file(s) uploaded successfully!`);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (data) => {
    setApiError(null);
    // Map franchiseCode to fseCode and convert gstRegistered
    const payload = {
        ...data,
        fseCode: data.franchiseCode, // Map to fseCode
        gstRegistered: data.gstRegistered === 'Yes'
    };
    delete payload.franchiseCode; // Remove the original franchiseCode key
    
    // Ensure xeroContactId is included if provided, otherwise it won't be in payload
    if (data.xeroContactId) {
        payload.xeroContactId = data.xeroContactId;
    } else {
        delete payload.xeroContactId; // Ensure it's not sent if empty
    }

    // Include attachments if there are uploaded files
    if (uploadedFiles.length > 0) {
      payload.attachments = uploadedFiles.map(file => ({
        type: file.category,  // API expects 'type' field
        customType: file.customType || null,
        url: file.url,
        fileName: file.name,
        size: file.size,
        expiryDate: file.expiryDate || null
      }));
    }

    try {
      await addFranchisee(payload); // Send payload with fseCode
      
      // Scroll to top before navigation to ensure user sees success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      navigate('/franchisees', { state: { message: 'Franchisee added successfully!' } });
    } catch (error) {
      // Duplicate franchise name: prompt admin to suspend the existing franchisee
      if (error instanceof DuplicateFranchiseeNameError && error.existingFranchisee) {
        setDuplicateNameModal({ show: true, existingFranchisee: error.existingFranchisee, pendingPayload: payload });
        return;
      }
      setApiError(error.message || 'Failed to add franchisee. Please try again.');
      console.error("Submission error:", error);
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // If franchisee creation failed but files were uploaded, clean them up from server
      // but keep them visible in UI so user doesn't have to re-upload
      if (uploadedFiles.length > 0) {
        console.log('Franchisee creation failed, cleaning up uploaded files from server...');
        try {
          await Promise.all(uploadedFiles.map(file => deleteFile(file.url)));
          console.log('Server files cleaned up successfully');
          // Don't clear uploadedFiles from UI - user should still see them
          // setUploadedFiles([]); // Commented out - keep files visible
        } catch (cleanupError) {
          console.error('Failed to clean up uploaded files from server:', cleanupError);
          // Even if cleanup fails, don't clear the UI - user should still see their files
        }
      }
    }
  };

  // --- Duplicate Franchise Name Handlers ---
  const handleSuspendAndProceed = async () => {
    const { existingFranchisee, pendingPayload } = duplicateNameModal;
    if (!existingFranchisee || !pendingPayload) return;
    setIsSuspendingFranchisee(true);
    try {
      // 1. Suspend the existing franchisee
      await updateFranchiseeStatus(existingFranchisee.id, false);
      // 2. Prepend 'Z' to the existing franchisee's name
      await editFranchisee(existingFranchisee.id, { franchiseName: 'Z' + existingFranchisee.franchiseName });
      // 3. Close modal
      setDuplicateNameModal({ show: false, existingFranchisee: null, pendingPayload: null });
      // 4. Retry adding the new franchisee
      await addFranchisee(pendingPayload);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate('/franchisees', { state: { message: 'Franchisee added successfully! Previous franchisee has been suspended.' } });
    } catch (err) {
      setDuplicateNameModal({ show: false, existingFranchisee: null, pendingPayload: null });
      setApiError(err.message || 'Failed to proceed. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSuspendingFranchisee(false);
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateNameModal({ show: false, existingFranchisee: null, pendingPayload: null });
  };

  return (
    <div className="container mx-auto px-4 pt-8 pb-20 max-w-3xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-jims-cyan-dark">Add New Franchisee</h1>
        <Link to="/franchisees" className="text-jims-cyan-dark hover:text-jims-cyan underline">
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-8 rounded-lg shadow-md space-y-6">

        {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}

        {/* Required Fields */}

        {/* Franchise Code and Name Row */}
        <div className="grid grid-cols-3 gap-6">
            <div className="col-span-1">
                <label htmlFor="franchiseCode" className="block text-sm font-medium text-gray-700">Franchise Code *</label>
                <input
                    id="franchiseCode"
                    type="text"
                    {...register("franchiseCode", { required: "Franchise Code is required." })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.franchiseCode ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.franchiseCode && <p className="mt-1 text-xs text-red-600">{errors.franchiseCode.message}</p>}
            </div>
            <div className="col-span-2">
                <label htmlFor="franchiseName" className="block text-sm font-medium text-gray-700">Franchise Name *</label>
                <input
                    id="franchiseName"
                    type="text"
                    {...register("franchiseName", { required: "Franchise Name is required." })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.franchiseName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.franchiseName && <p className="mt-1 text-xs text-red-600">{errors.franchiseName.message}</p>}
            </div>
        </div>

        {/* External Franchisee Checkbox */}
        <div className="border-t pt-4 mt-4 border-gray-200">
            <div className="flex items-center">
                <input
                    type="checkbox"
                    id="isExternal"
                    {...register("isExternal")}
                    className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                />
                <label htmlFor="isExternal" className="ml-2 block text-sm text-gray-700">
                    External Franchisee
                </label>
                <span className="ml-2 text-xs text-gray-500">
                    (Check if this franchisee is external to the region)
                </span>
            </div>
        </div>

        {/* Contact Person Fields - Moved Up */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name *</label>
                <input
                    id="firstName"
                    type="text"
                    {...register("firstName", { required: "First Name is required." })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>}
            </div>
            <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name *</label>
                <input
                    id="lastName"
                    type="text"
                    {...register("lastName", { required: "Last Name is required." })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName.message}</p>}
            </div>
        </div>

        {/* Email and Phone Number Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email *</label>
                <input
                    id="email"
                    type="email"
                    {...register("email", {
                    required: "Email is required.",
                    pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address."
                    }
                    })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>
            <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Phone Number *</label>
                <input
                    id="phoneNumber"
                    type="tel"
                    {...register("phoneNumber", { required: "Phone Number is required." })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.phoneNumber && <p className="mt-1 text-xs text-red-600">{errors.phoneNumber.message}</p>}
            </div>
        </div>

        {/* ABN and GST Registered Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <label htmlFor="abn" className="block text-sm font-medium text-gray-700">ABN *</label>
                <input
                    id="abn"
                    type="text"
                    {...register("abn", { required: "ABN is required." })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.abn ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.abn && <p className="mt-1 text-xs text-red-600">{errors.abn.message}</p>}
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">GST Registered *</label>
                <div className="mt-2 flex items-center space-x-4">
                    <label className="inline-flex items-center">
                        <input
                            type="radio"
                            value="Yes"
                            {...register("gstRegistered", { required: "Please select GST registration status." })}
                            className="form-radio h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Yes</span>
                    </label>
                    <label className="inline-flex items-center">
                        <input
                            type="radio"
                            value="No"
                            {...register("gstRegistered", { required: "Please select GST registration status." })}
                            className="form-radio h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">No</span>
                    </label>
                </div>
                {errors.gstRegistered && <p className="mt-1 text-xs text-red-600">{errors.gstRegistered.message}</p>}
            </div>
        </div>

        {/* BSB and Bank Account Number Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <label htmlFor="bsbNumber" className="block text-sm font-medium text-gray-700">BSB Number *</label>
                <input
                    id="bsbNumber"
                    type="text"
                    {...register("bsbNumber", { required: "BSB Number is required." })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.bsbNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.bsbNumber && <p className="mt-1 text-xs text-red-600">{errors.bsbNumber.message}</p>}
            </div>
            <div>
                <label htmlFor="bankAccountNumber" className="block text-sm font-medium text-gray-700">Bank Account Number *</label>
                <input
                    id="bankAccountNumber"
                    type="text"
                    {...register("bankAccountNumber", { required: "Bank Account Number is required." })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.bankAccountNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.bankAccountNumber && <p className="mt-1 text-xs text-red-600">{errors.bankAccountNumber.message}</p>}
            </div>
        </div>

        {/* Bank Account Name Row */}
        <div>
            <label htmlFor="bankAccountName" className="block text-sm font-medium text-gray-700">Bank Account Name *</label>
            <input
                id="bankAccountName"
                type="text"
                {...register("bankAccountName", { required: "Bank Account Name is required." })}
                className={`mt-1 block w-full px-3 py-2 border ${errors.bankAccountName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.bankAccountName && <p className="mt-1 text-xs text-red-600">{errors.bankAccountName.message}</p>}
        </div>

        {/* Xero Contact ID Field - ADDED */}
        <div>
            <label htmlFor="xeroContactId" className="block text-sm font-medium text-gray-700">Xero Contact ID</label>
            <input
                id="xeroContactId"
                type="text"
                {...register("xeroContactId")}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
            />
        </div>

        {/* Address and Optional Fields */}
        <div className="border-t pt-6 mt-6 border-gray-200">
            <div className="mb-6">
                <label htmlFor="territory" className="block text-sm font-medium text-gray-700">Territory</label>
                <input
                    id="territory"
                    type="text"
                    {...register("territory")}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <AddressAutocomplete
                    control={control}
                    setValue={setValue}
                />
                <div>
                    <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700">Address Line 2</label>
                    <input
                        id="addressLine2"
                        type="text"
                        {...register("addressLine2")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700">State</label>
                    <input
                        id="state"
                        type="text"
                        {...register("state")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">Postal Code</label>
                    <input
                        id="postalCode"
                        type="text"
                        {...register("postalCode")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country</label>
                    <input
                        id="country"
                        type="text"
                        {...register("country")}
                        readOnly
                        value="Australia"
                        className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm bg-gray-100 cursor-not-allowed`}
                    />
                </div>
            </div>
        </div>

        {/* File Upload Section */}
        <div className="border-t pt-6 mt-6 border-gray-200">
          <p className="text-sm text-gray-600 mb-3">File Attachments:</p>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="*/*"
          />

          {/* Attach File Link */}
          <div className="mb-4">
            <button
              type="button"
              onClick={handleAttachFile}
              className="inline-flex items-center text-jims-cyan hover:text-jims-cyan-dark font-medium text-sm"
            >
              <FaPaperclip className="mr-2" />
              Attach File
            </button>
            <span className="ml-2 text-xs text-gray-500">(Max 10MB per file)</span>
          </div>

          {/* Display attached files (to be uploaded) */}
          {attachedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Files to Upload:</h4>
              {attachedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        File Type *
                      </label>
                      <select
                        value={file.category}
                        onChange={(e) => updateFileCategory(file.id, e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan"
                        disabled={loadingFileTypes}
                      >
                        {loadingFileTypes ? (
                          <option>Loading file types...</option>
                        ) : (
                          fileTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    {isCustomFileType(file.category) && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Custom Type *
                        </label>
                        <input
                          type="text"
                          value={file.customType}
                          onChange={(e) => updateFileCustomType(file.id, e.target.value)}
                          placeholder="Specify the document type"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Expiry Date *
                      </label>
                      <input
                        type="date"
                        value={file.expiryDate}
                        onChange={(e) => updateFileExpiryDate(file.id, e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Upload Files Button - right after files to upload */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleUploadFiles}
                  disabled={uploading}
                  className="inline-flex items-center px-4 py-2 bg-jims-cyan text-white text-sm font-medium rounded-md hover:bg-jims-cyan-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaFileUpload className="mr-2" />
                  {uploading ? 'Uploading...' : `Upload ${attachedFiles.length} File(s)`}
                </button>
              </div>
            </div>
          )}

          {/* Display uploaded files */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Uploaded Files:</h4>
              {uploadedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => handleViewFile(file.url)}
                          className="text-sm font-medium text-jims-cyan hover:text-jims-cyan-dark underline truncate block text-left"
                          title="Click to download file"
                        >
                          {file.name}
                        </button>
                        <p className="text-xs text-gray-500">
                          {file.size > 0 ? formatFileSize(file.size) : 'Size unknown'} •
                          Type: {file.category === 'other' && file.customType ? file.customType : fileTypes.find(type => type.value === file.category)?.label}
                          {file.expiryDate && ` • Expires: ${new Date(file.expiryDate).toLocaleDateString('en-AU')}`}
                        </p>
                        <p className="text-xs text-green-600 font-medium">
                          ✓ Uploaded successfully
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUploadedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded-md">
              {uploadError}
            </div>
          )}
        </div>

        {/* Confirmation Modal for File Deletion */}
        {deleteConfirmModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirm File Deletion
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete "{deleteConfirmModal.file?.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Franchise Name Modal */}
        {duplicateNameModal.show && duplicateNameModal.existingFranchisee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Franchise Name Already Exists
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                A franchisee with this name already exists. Do you want to suspend the existing franchisee and proceed with adding the new one?
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-6 space-y-1 text-sm">
                <div><span className="font-medium text-gray-700">Code:</span> <span className="text-gray-900">{duplicateNameModal.existingFranchisee.fseCode}</span></div>
                <div><span className="font-medium text-gray-700">Franchise Name:</span> <span className="text-gray-900">{duplicateNameModal.existingFranchisee.franchiseName}</span></div>
                <div><span className="font-medium text-gray-700">Name:</span> <span className="text-gray-900">{duplicateNameModal.existingFranchisee.firstName} {duplicateNameModal.existingFranchisee.lastName}</span></div>
              </div>
              <p className="text-xs text-gray-500 mb-5">
                Choosing <strong>Yes</strong> will suspend the existing franchisee and rename them to <strong>Z{duplicateNameModal.existingFranchisee.franchiseName}</strong>, then save the new franchisee.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelDuplicate}
                  disabled={isSuspendingFranchisee}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={handleSuspendAndProceed}
                  disabled={isSuspendingFranchisee}
                  className="px-4 py-2 text-sm font-medium text-white bg-jims-cyan rounded-md hover:bg-jims-cyan-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan disabled:opacity-50"
                >
                  {isSuspendingFranchisee ? 'Processing...' : 'Yes, Suspend & Proceed'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sticky Action Bar
          Variant A (active): full viewport width
          Variant B (swap): replace this div with:
            <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              <div className="container mx-auto px-6 py-3 max-w-3xl flex justify-end gap-3">
                ...buttons...
              </div>
            </div>
        */}
        <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              <div className="container mx-auto px-6 py-3 max-w-3xl flex justify-end gap-3">
          <Link to="/franchisees" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded transition duration-300">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-300 disabled:opacity-50"
          >
            {isSubmitting ? 'Adding...' : 'Add Franchisee'}
          </button>
            </div>
        </div>
      </form>
    </div>
  );
};

export default AddFranchiseePage;
